import { useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import type { SavedMatchItem } from "../types/api";
import { KIND_LABELS, day, notApplied, outcomeLabel, scopeOf, toMarkdown } from "../lib/savedMatchesExport";
import HeaderFilter from "./HeaderFilter";
import { monthLabel } from "./ReconMonthDialog";
import { useColumnWidths } from "./useColumnWidths";

// Superadmin "Saved matches": every entry of the four per-insurer stores (manual
// matches, prior-period pairs, released auto-matches, rejected clears) across insurers
// and books, current AND removed, with who saved/removed it. Strictly read-only — there
// is no admin route that writes these stores. Copy/Download carry the stored entry JSON
// verbatim, so a paste or a backup holds the exact keys that auto-apply.

const COLS = ["Status", "Insurer", "Book", "Kind", "Shape", "CRI side", "Insurer side",
  "Saved by", "Saved on", "Removed by", "Last run", "Outcome"];

const PAGE = 200;

type Legs = { cri: string[]; ins: string[]; shape: string; note: string };
type Row = { item: SavedMatchItem; d: Legs; vals: Record<string, string[]>; hay: string };

const asStr = (v: unknown) => (typeof v === "string" ? v : "");
const asList = (v: unknown) => (Array.isArray(v) ? v.filter((s): s is string => typeof s === "string" && !!s) : []);

// "CRI|PJ/1|ALPHA|DEBITNOTE|32.86" -> "PJ/1 · ALPHA · DEBITNOTE · 32.86"
function pretty(sig: string): string {
  return sig.replace(/^(CRI|INS|UNREC)\|/, "").split("|").join(" · ");
}

// Which column a signature reads in: its own prefix says, else the carried list it sits on.
function bySide(sigs: string[], fallback: "cri" | "ins", prefix = ""): Pick<Legs, "cri" | "ins"> {
  const out = { cri: [] as string[], ins: [] as string[] };
  for (const s of sigs) {
    const side = s.startsWith("CRI|") ? "cri" : s.startsWith("INS|") ? "ins" : fallback;
    out[side].push(prefix + pretty(s));
  }
  return out;
}

function describe(item: SavedMatchItem): Legs {
  const e = item.entry;
  if (item.kind === "manual") {
    const cs = asList(e.cri_sigs), is = asList(e.insurer_sigs);
    if (cs.length + is.length > 0) {
      return { cri: cs.map(pretty), ins: is.map(pretty), shape: `Group ${cs.length} : ${is.length}`, note: "" };
    }
    if (asStr(e.a_sig)) {
      return { ...bySide([asStr(e.a_sig), asStr(e.b_sig)], "cri"), shape: "Same-book", note: "" };
    }
    return { cri: [pretty(asStr(e.cri_sig))], ins: [pretty(asStr(e.insurer_sig))], shape: "1:1", note: "" };
  }
  if (item.kind === "carried") {
    const basket = e.basket as { items?: Record<string, unknown>; rows?: Record<string, unknown> } | undefined;
    if (basket && typeof basket === "object") {
      const it = basket.items ?? {}, rw = basket.rows ?? {};
      const ci = asList(it.cri), ii = asList(it.insurer), cr = asList(rw.cri), ir = asList(rw.insurer);
      return { cri: [...ci.map((s) => "Prior: " + pretty(s)), ...cr.map((s) => "This period: " + pretty(s))],
               ins: [...ii.map((s) => "Prior: " + pretty(s)), ...ir.map((s) => "This period: " + pretty(s))],
               shape: `${ci.length + ii.length} prior : ${cr.length + ir.length} rows (both books)`, note: "" };
    }
    if (asStr(e.insurer_carried_sig)) {
      return { cri: ["Prior: " + pretty(asStr(e.cri_carried_sig))],
               ins: ["Prior: " + pretty(asStr(e.insurer_carried_sig))], shape: "Prior : prior", note: "" };
    }
    if (asList(e.insurer_carried_sigs).length) {
      const ci = asList(e.cri_carried_sigs), ii = asList(e.insurer_carried_sigs);
      return { cri: ci.map((s) => "Prior: " + pretty(s)), ins: ii.map((s) => "Prior: " + pretty(s)),
               shape: `Prior ${ci.length} : ${ii.length}`, note: "" };
    }
    const list = asStr(e.book) === "cri" ? "cri" : "ins";
    const side = asStr(e.side) === "cri" ? "cri" : "ins";
    const prior = asList(e.carried_sigs).length ? asList(e.carried_sigs) : [asStr(e.carried_sig)].filter(Boolean);
    const rows = asList(e.row_sigs).length ? asList(e.row_sigs) : [asStr(e.row_sig)].filter(Boolean);
    const p = bySide(prior, list, "Prior: "), r = bySide(rows, side, "This period: ");
    return { cri: [...p.cri, ...r.cri], ins: [...p.ins, ...r.ins],
             shape: `${prior.length} prior : ${rows.length} row${rows.length === 1 ? "" : "s"}`, note: "" };
  }
  if (item.kind === "released") {
    return { ...bySide(asList(e.sigs), "ins"), shape: "Released", note: asStr(e.reason) };
  }
  const list = asStr(e.list);
  const sig = "Prior: " + pretty(asStr(e.sig));
  return { cri: list === "cri" ? [sig] : [], ins: list === "cri" ? [] : [sig],
           shape: list ? `${list === "cri" ? "CRI" : "Insurer"} list` : "Both lists", note: asStr(e.reason) };
}

function when(t: string): string {
  return t ? new Date(t).toLocaleString() : "";
}

// What each column's header filter offers and matches for one entry. A cell with several
// legs passes when any of them is ticked.
function valuesOf(i: SavedMatchItem, d: Legs): Record<string, string[]> {
  const one = (v: string) => (v ? [v] : []);
  return {
    Status: [i.status === "active" ? "Active" : "Removed"],
    Insurer: [i.insurer],
    Book: [scopeOf(i)],
    Kind: [KIND_LABELS[i.kind]],
    Shape: one(d.shape),
    "CRI side": d.cri,
    "Insurer side": d.ins,
    "Saved by": [i.saved_by || (i.saved_at ? "unknown user" : "Saved before history began")],
    "Saved on": one(day(i.saved_at)),
    "Removed by": i.status === "removed" ? [i.removed_by || "unknown user"] : [],
    "Last run": one(i.last_run?.month ?? ""),
    Outcome: one(outcomeLabel(i)),
  };
}

function Outcome({ item }: { item: SavedMatchItem }) {
  const r = item.last_run;
  if (!r) {
    return <span className="muted">{outcomeLabel(item) || "—"}</span>;
  }
  return (
    <>
      <span className={`badge ${r.applied ? (r.status.startsWith("Held") ? "badge-warn" : "badge-success") : "badge-risk"}`}>
        {outcomeLabel(item)}
      </span>
      {r.reason && <div className="muted">{r.reason}</div>}
    </>
  );
}

const legList = (legs: string[]) => legs.length
  ? legs.map((s, k) => <div key={k} style={{ fontFamily: "monospace" }}>{s}</div>)
  : <span className="muted">—</span>;

export default function SavedMatchesTab() {
  const [items, setItems] = useState<SavedMatchItem[] | null>(null);
  const [historyBytes, setHistoryBytes] = useState(0);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [openCol, setOpenCol] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [copied, setCopied] = useState<"" | "ok" | "blocked">("");
  const [copyText, setCopyText] = useState("");

  async function load() {
    setError("");
    try {
      const r = await api.adminSavedMatches();
      setItems(r.items);
      setHistoryBytes(r.history_bytes);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load saved matches.");
    }
  }
  useEffect(() => { load(); }, []);

  const all = items ?? [];
  // Described and indexed once per load, not per keystroke or filter tick.
  const rows = useMemo<Row[]>(() => all.map((item) => {
    const d = describe(item);
    return { item, d, vals: valuesOf(item, d), hay: JSON.stringify(item).toLowerCase() };
  }), [all]);
  const options = useMemo(() => {
    const cache = new Map<string, string[]>();
    return (c: string) => {
      let o = cache.get(c);
      if (!o) {
        o = [...new Set(rows.flatMap((r) => r.vals[c]))].sort();
        cache.set(c, o);
      }
      return o;
    };
  }, [rows]);
  const nActive = Object.values(filters).filter((v) => v.length).length;
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const act = Object.entries(filters).filter(([, v]) => v.length).map(([c, v]) => [c, new Set(v)] as const);
    return rows.filter((r) => act.every(([c, set]) => r.vals[c].some((v) => set.has(v)))
      && (!q || r.hay.includes(q)));
  }, [rows, filters, query]);
  useEffect(() => { setPage(0); }, [filters, query, rows]);
  const nPages = Math.max(1, Math.ceil(shown.length / PAGE));
  const pageRows = shown.slice(page * PAGE, (page + 1) * PAGE);

  const active = all.filter((i) => i.status === "active").length;
  const ran = all.filter((i) => i.last_run).length;
  const missed = all.filter(notApplied).length;
  const insurers = useMemo(() => new Set(all.map((i) => i.insurer)).size, [all]);
  const cw = useColumnWidths(COLS, items);

  // Copy and Download hand over the same Markdown: a paste and a file read the same way.
  function copyAll() {
    const text = toMarkdown(shown.map((r) => r.item), all.length);
    setCopyText(text);
    // Clipboard needs a secure origin; when it is blocked, the text is shown to copy by hand.
    if (!navigator.clipboard) { setCopied("blocked"); return; }
    navigator.clipboard.writeText(text).then(() => setCopied("ok"), () => setCopied("blocked"));
  }

  function download() {
    const blob = new Blob([toMarkdown(shown.map((r) => r.item), all.length)], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `saved-matches-${new Date().toISOString().slice(0, 10)}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (error) return <div className="badge badge-risk" style={{ display: "block" }}>{error}</div>;
  if (!items) return <p className="muted">Loading saved matches…</p>;

  return (
    <section className="card" style={{ padding: 20 }}>
      <div className="page-head" style={{ marginBottom: 12 }}>
        <h2 className="card-title" style={{ margin: 0, fontSize: "1rem" }}>Saved matches</h2>
        <button className="btn" onClick={load}>Refresh</button>
      </div>
      <div className="chip-row" style={{ marginBottom: 12 }}>
        <span className="chip">{active} active</span>
        <span className="chip">{all.length - active} removed</span>
        <span className="chip">{insurers} insurers</span>
        <span className="chip">Newest runs: {ran - missed} applied · {missed} not applied</span>
        <span className="chip">History on disk {historyBytes < 1024 * 1024
          ? `${(historyBytes / 1024).toFixed(1)} KB` : `${(historyBytes / 1024 / 1024).toFixed(2)} MB`}</span>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <input className="input" placeholder="Policy, name, amount, user…" value={query}
          onChange={(e) => setQuery(e.target.value)} style={{ flex: 1, minWidth: 180 }} />
        {nActive > 0 && (
          <button className="btn" onClick={() => setFilters({})}>
            Clear {nActive} filter{nActive === 1 ? "" : "s"}
          </button>
        )}
        <button className="btn" onClick={copyAll} disabled={!shown.length}>Copy all shown ({shown.length})</button>
        <button className="btn" onClick={download} disabled={!shown.length}>Download (.md)</button>
      </div>

      {copied === "ok" && <div className="badge badge-success" style={{ display: "block", marginBottom: 10 }}>
        Copied {shown.length} entries to the clipboard.</div>}
      {copied === "blocked" && (
        <div style={{ marginBottom: 10 }}>
          <div className="muted note-tight">The browser blocked clipboard access. Select the text below and copy it.</div>
          <textarea className="input" readOnly value={copyText} rows={8} style={{ width: "100%", fontFamily: "monospace" }}
            onFocus={(e) => e.currentTarget.select()} />
        </div>
      )}

      <div style={{ overflowX: "auto" }}>
        <table className={"data-table" + cw.className} style={cw.style}>
          {cw.colgroup}
          <thead>
            <tr>{COLS.map((c) => (
              <th key={c} className="nowrap">
                <span className="th-inner">
                  <span>{c}</span>
                  <HeaderFilter
                    active={(filters[c]?.length ?? 0) > 0}
                    open={openCol === c}
                    optionsFor={() => options(c)}
                    selected={filters[c] ?? []}
                    onToggleOpen={() => setOpenCol(openCol === c ? null : c)}
                    onChange={(v) => setFilters((f) => ({ ...f, [c]: v }))}
                    dateColumn={c === "Saved on"}
                    format={c === "Last run" ? monthLabel : undefined} />
                </span>
                {cw.grip(c)}
              </th>
            ))}</tr>
          </thead>
          <tbody>
            {pageRows.map(({ item: i, d }, n) => (
              <tr key={page * PAGE + n}>
                <td>
                  <span className={`badge ${i.status === "active" ? "badge-success" : "badge-risk"}`}>
                    {i.status === "active" ? "Active" : "Removed"}
                  </span>
                </td>
                <td>{i.insurer}</td>
                <td>{scopeOf(i)}</td>
                <td>{KIND_LABELS[i.kind]}</td>
                <td>{d.shape}</td>
                <td>
                  {legList(d.cri)}
                  {(i.pins ?? []).filter((p) => p.side === "cri").map((p, k) =>
                    <div key={k} className="muted">Identified by {p.label} = {p.value}</div>)}
                </td>
                <td>
                  {legList(d.ins)}
                  {(i.pins ?? []).filter((p) => p.side === "insurer").map((p, k) =>
                    <div key={k} className="muted">Identified by {p.label} = {p.value}</div>)}
                  {d.note && <div className="muted">Reason: {d.note}</div>}
                </td>
                <td>
                  {i.saved_by || i.saved_at ? i.saved_by || "unknown user"
                    : <span className="muted">Saved before history began</span>}
                </td>
                <td>{i.saved_at ? when(i.saved_at) : <span className="muted">—</span>}</td>
                <td>
                  {i.status === "removed"
                    ? <><div style={{ color: "var(--risk)" }}>{i.removed_by || "unknown user"}</div>
                        <div className="muted">{when(i.removed_at)}</div></>
                    : <span className="muted">—</span>}
                </td>
                <td>
                  {i.last_run
                    ? <><div>{monthLabel(i.last_run.month)}</div>
                        <div className="muted">{i.last_run.run_by || "unknown user"} · {when(i.last_run.run_at)}</div></>
                    : <span className="muted">—</span>}
                </td>
                <td><Outcome item={i} /></td>
              </tr>
            ))}
            {!shown.length && <tr><td colSpan={COLS.length} className="muted">No saved matches for these filters.</td></tr>}
          </tbody>
        </table>
      </div>
      {shown.length > PAGE && (
        <div className="grid-pager">
          <button className="btn" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>Prev</button>
          <span className="muted">Page {page + 1} of {nPages} · {shown.length.toLocaleString()} entries</span>
          <button className="btn" onClick={() => setPage((p) => Math.min(nPages - 1, p + 1))} disabled={page >= nPages - 1}>Next</button>
        </div>
      )}
      <p className="muted note">
        Read-only. Showing {shown.length} of {all.length}. Admins cannot add, edit, or delete
        saved matches here; finance manages them from the reconciliation screens.
      </p>
    </section>
  );
}
